import { apiDelete, apiGet, apiPatch, apiPost, apiPostForm, apiPostFormWithProgress, apiPostWithStatus } from './client';
import type { components } from './types.gen';

type Schemas = components['schemas'];
type Page<T> = Schemas['Page'] & { items: T[] };

export type MeDto = Schemas['Me'];
export type VehicleDto = Schemas['Vehicle'];
export type AlertDto = Schemas['Alert'];
export type AlertCanonicalTypeDto = Schemas['AlertCanonicalType'];
export type SubunitDto = Schemas['Subunit'];
export type DriverDto = Schemas['Driver'];
export type DriverCreateDto = Schemas['DriverCreate'];
export type UserDto = Schemas['User'];
export type UserCreateDto = Schemas['UserCreate'];
export type UserUpdateDto = Schemas['UserUpdate'];
export type UserCreatedDto = Schemas['UserCreated'];
export type VehicleCreateDto = Schemas['VehicleCreate'];
export type VehicleUpdateDto = Schemas['VehicleUpdate'];
export type SubunitCreateDto = Schemas['SubunitCreate'];
export type DriverAssignmentDto = Schemas['DriverAssignment'];
export type DriverAssignmentCreateDto = Schemas['DriverAssignmentCreate'];
export type DeviceDto = Schemas['Device'];
export type DeviceInstallationDto = Schemas['DeviceInstallation'];
export type DeviceInstallationCreateDto = Schemas['DeviceInstallationCreate'];
export type CaptureModeDto = Schemas['CaptureMode'];
export type DeviceCameraDto = Schemas['DeviceCamera'];
export type DeviceCameraCreateDto = Schemas['DeviceCameraCreate'];
export type CameraLabelDto = Schemas['CameraLabel'];
export type CameraProbeItemDto = Schemas['CameraProbeItem'];
export type EvidenceDto = Schemas['Evidence'];
export type AlertEvidenceDto = Schemas['AlertEvidence'];
export type RankingEntryDto = Schemas['RankingEntry'];
export type OwnScoreDto = Schemas['OwnScore'];
export type RecordingsAvailabilityDto = Schemas['RecordingsAvailability'];
export type AlertSourceDto = Schemas['AlertSource'];
export type TripDto = Schemas['Trip'];
export type TripStatusDto = Schemas['TripStatus'];
export type TripCreateDto = Schemas['TripCreate'];
export type TripUpdateDto = Schemas['TripUpdate'];
export type TripTransitionDto = Schemas['TripTransition'];
export type TripStartDto = Schemas['TripStart'];
export type RouteDto = Schemas['Route'];
export type LogbookEntryDto = Schemas['LogbookEntry'];
export type LogbookEntryCreateDto = Schemas['LogbookEntryCreate'];
export type LogbookEntryPatchDto = Schemas['LogbookEntryPatch'];
export type LogbookEntryTypeDto = Schemas['LogbookEntryType'];
export type LogbookEntryTypeCreateDto = Schemas['LogbookEntryTypeCreate'];
export type LogbookEntryTypePatchDto = Schemas['LogbookEntryTypePatch'];
export type LogbookTransitionDto = Schemas['LogbookTransition'];
export type LogbookAttachmentDto = Schemas['LogbookAttachment'];

export type ExpenseDto = Schemas['Expense'];
export type ExpenseCreateDto = Schemas['ExpenseCreate'];
export type ExpensePatchDto = Schemas['ExpensePatch'];
export type ExpenseCategoryDto = Schemas['ExpenseCategory'];
export type ExpenseCategoryCreateDto = Schemas['ExpenseCategoryCreate'];
export type ExpenseCategoryPatchDto = Schemas['ExpenseCategoryPatch'];
export type ExpenseTransitionDto = Schemas['ExpenseTransition'];
export type ReceiptDto = Schemas['Receipt'];
export type CustomerDto = Schemas['Customer'];
export type CustomerCreateDto = Schemas['CustomerCreate'];
export type CustomerPatchDto = Schemas['CustomerPatch'];
export type TariffDto = Schemas['Tariff'];
export type TariffCreateDto = Schemas['TariffCreate'];
export type TariffPatchDto = Schemas['TariffPatch'];
export type TariffQuoteDto = Schemas['TariffQuote'];
export type RevenueDto = Schemas['Revenue'];
export type RevenueCreateDto = Schemas['RevenueCreate'];
export type RevenuePatchDto = Schemas['RevenuePatch'];
export type RevenueTransitionDto = Schemas['RevenueTransition'];
export type RevenueAttachmentDto = Schemas['RevenueAttachment'];
export type FinanceSummaryDto = Schemas['FinanceSummary'];
export type FinanceGroupByDto = Schemas['FinanceGroupBy'];
export type ReportDto = Schemas['Report'];
export type ReportRequestDto = Schemas['ReportRequest'];

export type ConversationDto = Schemas['Conversation'];
export type ChatParticipantDto = Schemas['ChatParticipant'];
export type MessageDto = Schemas['Message'];
export type MessageAttachmentDto = Schemas['MessageAttachment'];
export type ChatContactDto = Schemas['ChatContact'];

// El contrato pagina con pageSize máx. 200 (default 50) — un solo fetch sin
// recorrer páginas trunca en silencio en cuanto la colección pase de ese
// límite (encontrado en `/alerts`, PENDIENTES.md/FE-04). Recorre todas las
// páginas de cualquier endpoint paginado y devuelve el total real.
async function fetchAllPages<T>(fetchPage: (page: number, pageSize: number) => Promise<Page<T>>): Promise<T[]> {
  const pageSize = 200;
  const items: T[] = [];
  let page = 1;
  for (;;) {
    const res = await fetchPage(page, pageSize);
    items.push(...res.items);
    if (items.length >= res.totalItems || res.items.length === 0) break;
    page += 1;
  }
  return items;
}

// GET /me — docs/Paquete_Arranque_Frontend.md §3, paso 7. Reemplaza a
// lib/auth/me.ts de FE-01: mismo propósito, ahora sobre el cliente genérico.
export function getMe(): Promise<MeDto> {
  return apiGet<MeDto>('/me');
}

export function getSubunits(): Promise<{ items: SubunitDto[] }> {
  return apiGet<{ items: SubunitDto[] }>('/subunits');
}

let subunitNamesCache: Promise<Map<string, string>> | null = null;

// FE-04: /vehicles y /alerts no entregan subUnitName, solo subunitId — se
// resuelve con un mapa id->name pedido una sola vez por pestaña (las
// sub-unidades no cambian con la frecuencia de vehículos/alertas).
export function getSubunitNames(): Promise<Map<string, string>> {
  if (!subunitNamesCache) {
    subunitNamesCache = getSubunits().then(res => new Map(res.items.map(s => [s.id, s.name])));
  }
  return subunitNamesCache;
}

export interface VehiclesQuery {
  page?: number;
  pageSize?: number;
  subunitId?: string;
  fleetId?: string;
  state?: Schemas['VehicleState'];
}

export function getVehicles(query: VehiclesQuery = {}): Promise<Page<VehicleDto>> {
  return apiGet<Page<VehicleDto>>(`/vehicles${toQueryString(query)}`);
}

export function getAllVehicles(query: Omit<VehiclesQuery, 'page' | 'pageSize'> = {}): Promise<VehicleDto[]> {
  return fetchAllPages((page, pageSize) => getVehicles({ ...query, page, pageSize }));
}

export function getVehicle(vehicleId: string): Promise<VehicleDto> {
  return apiGet<VehicleDto>(`/vehicles/${vehicleId}`);
}

export function createVehicle(data: VehicleCreateDto): Promise<VehicleDto> {
  return apiPost<VehicleDto>('/vehicles', data);
}

export function updateVehicle(vehicleId: string, data: VehicleUpdateDto): Promise<VehicleDto> {
  return apiPatch<VehicleDto>(`/vehicles/${vehicleId}`, data);
}

export interface DriverAssignmentsQuery {
  page?: number;
  pageSize?: number;
  vehicleId?: string;
  driverId?: string;
  activeAt?: string;
}

export function getDriverAssignments(query: DriverAssignmentsQuery = {}): Promise<Page<DriverAssignmentDto>> {
  return apiGet<Page<DriverAssignmentDto>>(`/driver-assignments${toQueryString(query)}`);
}

export function createDriverAssignment(data: DriverAssignmentCreateDto): Promise<DriverAssignmentDto> {
  return apiPost<DriverAssignmentDto>('/driver-assignments', data);
}

// Cerrar (no borrar) es el único movimiento que libera el vehículo para una
// nueva asignación — el EXCLUDE de vigencias (ADR-007) rechaza el POST de
// reemplazo si la anterior sigue abierta.
export function closeDriverAssignment(assignmentId: string, validTo: string): Promise<DriverAssignmentDto> {
  return apiPatch<DriverAssignmentDto>(`/driver-assignments/${assignmentId}`, { validTo });
}

export function createSubunit(data: SubunitCreateDto): Promise<SubunitDto> {
  return apiPost<SubunitDto>('/subunits', data);
}

// El PATCH reutiliza el schema `SubunitCreate` (openapi.yaml) — la sub-unidad
// solo tiene `name`, no hay un `SubunitUpdate` separado.
export function updateSubunit(subunitId: string, data: SubunitCreateDto): Promise<SubunitDto> {
  return apiPatch<SubunitDto>(`/subunits/${subunitId}`, data);
}

// Mismo endpoint, dos usos (openapi.yaml): sin `to` es el patrón hidratar +
// acumular de FE-06 (`from` = inicio del viaje a hidratar por REST, los
// puntos nuevos llegan después por Firestore); con `to` es el playback
// histórico de FE-25 — una consulta puntual, sin suscripción.
export function getVehicleRoute(vehicleId: string, from: string, to?: string, maxPoints?: number): Promise<RouteDto> {
  return apiGet<RouteDto>(`/vehicles/${vehicleId}/route${toQueryString({ from, to, maxPoints })}`);
}

export interface DriversQuery {
  page?: number;
  pageSize?: number;
  subunitId?: string;
  fleetId?: string;
}

export function getDrivers(query: DriversQuery = {}): Promise<Page<DriverDto>> {
  return apiGet<Page<DriverDto>>(`/drivers${toQueryString(query)}`);
}

export function getAllDrivers(query: Omit<DriversQuery, 'page' | 'pageSize'> = {}): Promise<DriverDto[]> {
  return fetchAllPages((page, pageSize) => getDrivers({ ...query, page, pageSize }));
}

// Alta de conductor (issue #196). El contrato pide solo `displayName`;
// `documentId`/`phone`/`subunitId` son opcionales. El frontend no lo usaba:
// en un tenant limpio no había forma de crear el conductor que exige el alta
// de un Operador. Lo consume el formulario de Operador (crea conductor y
// luego la cuenta con ese `driverId`).
export function createDriver(data: DriverCreateDto): Promise<DriverDto> {
  return apiPost<DriverDto>('/drivers', data);
}

export interface RankingsQuery {
  fleetId?: string;
  subunitId?: string;
  from?: string;
  to?: string;
}

// BE-22 (`PRODUCT.md` §4.4). Solo Admin/Supervisor/Ejecutivo — el Operador
// recibe 403 y consulta su propio score en `getMeScore`.
export function getRankings(query: RankingsQuery = {}): Promise<{ period?: string; items: RankingEntryDto[] }> {
  return apiGet<{ period?: string; items: RankingEntryDto[] }>(`/rankings${toQueryString(query)}`);
}

// 404 si el usuario no tiene conductor vinculado (un Administrador no
// conduce) — el llamador lo trata igual que "sin score", no como un error.
export function getMeScore(): Promise<OwnScoreDto> {
  return apiGet<OwnScoreDto>('/me/score');
}

export interface UsersQuery {
  page?: number;
  pageSize?: number;
  role?: Schemas['Role'];
  subunitId?: string;
}

export function getUsers(query: UsersQuery = {}): Promise<Page<UserDto>> {
  return apiGet<Page<UserDto>>(`/users${toQueryString(query)}`);
}

export function getAllUsers(query: Omit<UsersQuery, 'page' | 'pageSize'> = {}): Promise<UserDto[]> {
  return fetchAllPages((page, pageSize) => getUsers({ ...query, page, pageSize }));
}

// El core crea el usuario en Identity Platform y le MANDA UN CORREO para que
// defina su contraseña. El 201 ya no trae ninguna credencial: trae
// `invitation { status, sentAt, detail }` — openapi.yaml `UserCreated`.
//
// `status: FAILED` llega con 201, no con error: el usuario SÍ quedó creado y
// lo que falta es reenviar, no repetir el alta (que chocaría con el 409).
export function createUser(data: UserCreateDto): Promise<UserCreatedDto> {
  return apiPost<UserCreatedDto>('/users', data);
}

// Reenvía el correo de definición de contraseña. 202 porque quien entrega el
// correo es Identity Platform: el core no puede afirmar que llegó.
// 409 si el usuario está INACTIVE — no se invita a quien está de baja.
export function resendUserInvitation(userId: string): Promise<void> {
  return apiPost<void>(`/users/${userId}/invitation`, undefined);
}

export function updateUser(userId: string, data: UserUpdateDto): Promise<UserDto> {
  return apiPatch<UserDto>(`/users/${userId}`, data);
}

export interface AlertsQuery {
  page?: number;
  pageSize?: number;
  status?: Schemas['AlertStatus'];
  severity?: Schemas['Severity'];
  category?: string;
  vehicleId?: string;
  driverId?: string;
  subunitId?: string;
  from?: string;
  to?: string;
}

export function getAlerts(query: AlertsQuery = {}): Promise<Page<AlertDto>> {
  return apiGet<Page<AlertDto>>(`/alerts${toQueryString(query)}`);
}

export function getAllAlerts(query: Omit<AlertsQuery, 'page' | 'pageSize'> = {}): Promise<AlertDto[]> {
  return fetchAllPages((page, pageSize) => getAlerts({ ...query, page, pageSize }));
}

export function getAlert(alertId: string): Promise<Schemas['AlertDetail']> {
  return apiGet<Schemas['AlertDetail']>(`/alerts/${alertId}`);
}

// Razón obligatoria — PRODUCT.md §4.2. 409 si ya estaba cerrada (ApiError,
// no una excepción distinta: el llamador distingue por `status`).
export function closeAlert(alertId: string, reason: string): Promise<AlertDto> {
  return apiPost<AlertDto>(`/alerts/${alertId}/close`, { reason });
}

export interface DevicesQuery {
  page?: number;
  pageSize?: number;
}

export function getDevices(query: DevicesQuery = {}): Promise<Page<DeviceDto>> {
  return apiGet<Page<DeviceDto>>(`/devices${toQueryString(query)}`);
}

export function getAllDevices(): Promise<DeviceDto[]> {
  return fetchAllPages((page, pageSize) => getDevices({ page, pageSize }));
}

// Instalación del equipo en un vehículo (issue #204). El contrato ya existía
// (ADR-007: la vigencia es un recurso aparte con EXCLUDE por vehículo); el
// frontend solo leía `GET /devices`. Sin instalación vigente el adaptador no
// sondea el equipo (`polling_targets` solo instalados) y el vehículo nunca
// recibe posición ni video.
export interface DeviceInstallationsQuery {
  deviceId?: string;
  vehicleId?: string;
  // Filtra las vigentes en ese instante (openapi.yaml).
  activeAt?: string;
}

export function getDeviceInstallations(query: DeviceInstallationsQuery = {}): Promise<Page<DeviceInstallationDto>> {
  return apiGet<Page<DeviceInstallationDto>>(`/device-installations${toQueryString(query)}`);
}

// 409 si la vigencia se solapa con otra sobre el MISMO vehículo (ADR-007) —
// el llamador lo distingue por `ApiError.status` y lo muestra.
export function createDeviceInstallation(data: DeviceInstallationCreateDto): Promise<DeviceInstallationDto> {
  return apiPost<DeviceInstallationDto>('/device-installations', data);
}

// Cerrar la vigencia = retirar el equipo del vehículo (PRODUCT.md §4.7).
export function closeDeviceInstallation(installationId: string, validTo: string): Promise<DeviceInstallationDto> {
  return apiPatch<DeviceInstallationDto>(`/device-installations/${installationId}`, { validTo });
}

// Grabación continua del equipo (issue #205). SOLO Administrador (acción
// `manage_capture`, ADR-027 §5.1): `CONTINUOUS` graba 24/7 y consume el plan
// de datos de la SIM, por eso nace en `OFF` y el Supervisor no puede cambiarlo.
// `Device.captureMode` expone el estado actual.
export function updateDeviceCapture(deviceId: string, mode: CaptureModeDto): Promise<DeviceDto> {
  return apiPatch<DeviceDto>(`/devices/${deviceId}/capture`, { mode });
}

// FE-14 — inventario de cámaras (ADR-018): no es descubrible por la API del
// proveedor, se declara a mano con ayuda de la sonda de abajo.
export function getDeviceCameras(deviceId: string): Promise<{ items: DeviceCameraDto[] }> {
  return apiGet<{ items: DeviceCameraDto[] }>(`/devices/${deviceId}/cameras`);
}

export function createDeviceCamera(deviceId: string, data: DeviceCameraCreateDto): Promise<DeviceCameraDto> {
  return apiPost<DeviceCameraDto>(`/devices/${deviceId}/cameras`, data);
}

export function updateDeviceCamera(
  deviceId: string,
  cameraId: string,
  data: { label?: CameraLabelDto; name?: string | null; enabled?: boolean },
): Promise<DeviceCameraDto> {
  return apiPatch<DeviceCameraDto>(`/devices/${deviceId}/cameras/${cameraId}`, data);
}

export function deleteDeviceCamera(deviceId: string, cameraId: string): Promise<void> {
  return apiDelete<void>(`/devices/${deviceId}/cameras/${cameraId}`);
}

// La sonda tarda (el core llama canal por canal al proveedor) y devuelve
// TODOS los canales a la vez, cada uno con su URL viva o su `error` — un
// canal fallido no tumba la sonda entera (openapi.yaml, ADR-018).
export function probeDeviceCameras(deviceId: string, channels?: number[]): Promise<{ items: CameraProbeItemDto[] }> {
  return apiPost<{ items: CameraProbeItemDto[] }>(
    `/devices/${deviceId}/camera-probe`,
    channels ? { channels } : undefined,
  );
}

// `cameraId` sale de `Vehicle.cameras[]` — opaco, nunca es el canal del
// proveedor (ADR-018). La URL devuelta es una capability efímera: no se
// persiste en ningún sitio (ADR-014 §6).
export function requestVehicleLiveStream(
  vehicleId: string,
  cameraId: string,
): Promise<{ playlistUrl: string; expiresAt: string; delaySeconds: number }> {
  return apiPost<{ playlistUrl: string; expiresAt: string; delaySeconds: number }>(
    `/vehicles/${vehicleId}/live-stream`,
    { cameraId },
  );
}

// BE-46: los tramos y huecos son la fuente de verdad de la línea de tiempo.
// `playlistUrl`, si llega, ya es una ruta `/v1/...` de esta API: el player la
// carga tal cual para no convertirla accidentalmente en `/v1/v1/...`.
export function getVehicleRecordings(
  vehicleId: string,
  cameraId: string,
  range: { from: string; to?: string },
): Promise<RecordingsAvailabilityDto> {
  return apiGet<RecordingsAvailabilityDto>(
    `/vehicles/${vehicleId}/cameras/${cameraId}/recordings${toQueryString(range)}`,
  );
}

// BE-52 (ADR-025): la evidencia de una alerta. `cameras[].clips` es lo DERIVADO
// (el video que hay en la ventana, reproducible sin haberlo conservado) e
// `items[]` lo CONSERVADO. `hasEvidence` de la alerta solo significa lo
// segundo: para saber si hay video se mira `coverage`.
export function getAlertEvidence(alertId: string): Promise<AlertEvidenceDto> {
  return apiGet<AlertEvidenceDto>(`/alerts/${alertId}/evidence`);
}

// SIN USO desde el 2026-09-20. Firma el `object_path` de lo CONSERVADO, que hoy
// es el `manifest.json` (ADR-025 §5), no un video: el botón de descarga que la
// llamaba bajaba un JSON. La descarga real de video la arma el navegador
// (`lib/api/evidenceClip.ts`) hasta que `BE-56` (#110) entregue un MP4 aquí;
// cuando exista, el botón vuelve a usar este endpoint y ese puente se retira.
//
// La URL es una capability efímera (mismo patrón que live-stream): no se
// persiste ni se reutiliza más allá de su `expiresAt`.
export function requestEvidenceDownloadUrl(evidenceId: string): Promise<{ url: string; expiresAt: string }> {
  return apiPost<{ url: string; expiresAt: string }>(`/evidence/${evidenceId}/download-url`);
}

// BE-45 — `POST /vehicles/{vehicleId}/panic`, SIN cuerpo: la emergencia se
// declara, no se describe (la foto va por BE-40 y el relato por BE-29).
//
// Es IDEMPOTENTE POR VEHÍCULO: mientras exista un `SOS` `ACTIVE` sobre él, un
// pulso nuevo no crea otra alerta, responde 200 con la que ya había. Por eso
// `alreadyActive` no es un error — es lo que la UI tiene que contarle al
// conductor, y es también lo que hace seguro reintentar un envío que falló por
// red: si en realidad sí llegó, el reintento devuelve la misma alerta y nunca
// una segunda.
//
// Permisos (PRODUCT.md §4.8): Operador solo sobre su vehículo asignado hoy,
// Supervisor en su sub-unidad, Administrador en todo el tenant, Ejecutivo no.
// Un vehículo fuera de alcance responde 404, no 403: la API no confirma su
// existencia.
export async function raisePanic(vehicleId: string): Promise<{ alert: AlertDto; alreadyActive: boolean }> {
  const res = await apiPostWithStatus<AlertDto>(`/vehicles/${vehicleId}/panic`);
  return { alert: res.data, alreadyActive: res.status === 200 };
}

// ---------------------------------------------------------------------------
// FE-11 — viajes (BE-27/BE-48, ADR-024). Los ocho endpoints del contrato.
// ---------------------------------------------------------------------------

export interface TripsQuery {
  page?: number;
  pageSize?: number;
  status?: TripStatusDto;
  vehicleId?: string;
  driverId?: string;
  subunitId?: string;
  from?: string;
  to?: string;
}

// ⚠️ `from`/`to` filtran por SOLAPE con la ventana del viaje, no por
// contención: un viaje que empezó ayer y sigue hoy aparece en la consulta de
// hoy. Es lo correcto para un histórico y lo contrario de lo que hacía el
// filtro sobre `MOCK_TRIPS`, que comparaba una fecha suelta.
export function getTrips(query: TripsQuery = {}): Promise<Page<TripDto>> {
  return apiGet<Page<TripDto>>(`/trips${toQueryString(query)}`);
}

export function getAllTrips(query: Omit<TripsQuery, 'page' | 'pageSize'> = {}): Promise<TripDto[]> {
  return fetchAllPages((page, pageSize) => getTrips({ ...query, page, pageSize }));
}

export function getTrip(tripId: string): Promise<TripDto> {
  return apiGet<TripDto>(`/trips/${tripId}`);
}

// 409 si el vehículo ya tiene otro viaje en esa ventana — lo rechaza el
// EXCLUDE de la base de datos (ADR-007), no una validación de aplicación. Sin
// esa regla, "¿a qué viaje pertenece esta posición?" no tendría respuesta
// única, así que el 409 es información de negocio, no un fallo técnico.
export function createTrip(data: TripCreateDto): Promise<TripDto> {
  return apiPost<TripDto>('/trips', data);
}

// Corrige un viaje, INCLUSO ya completado. `reason` es obligatoria: la
// corrección entra a la novedad con el rango anterior y el nuevo. Mover
// cualquiera de las cuatro marcas de tiempo mueve la ventana, y con ella el
// expediente entero — quien llame a esto debe volver a pedir ruta y alertas.
export function updateTrip(tripId: string, data: TripUpdateDto): Promise<TripDto> {
  return apiPatch<TripDto>(`/trips/${tripId}`, data);
}

// `at` es opcional: por defecto, ahora. Un inicio declarado tarde se registra
// con su hora real, y si eso pisara el viaje anterior responde 409.
// FE-34 (ADR-032): si el viaje lleva tarifa, `quantity` es el peso del
// manifiesto (obligatorio con tarifa por unidad: el core responde 400 sin él)
// y el core crea el ingreso en la misma transacción. Sin tarifa, el cuerpo
// extra se ignora. Se manda solo lo que tiene valor: `at` ausente = «ahora».
export function startTrip(tripId: string, data: TripStartDto = {}): Promise<TripDto> {
  const body: TripStartDto = {};
  if (data.at) body.at = data.at;
  if (data.quantity) body.quantity = data.quantity;
  if (data.reference) body.reference = data.reference;
  if (data.notes) body.notes = data.notes;
  return apiPost<TripDto>(`/trips/${tripId}/start`, Object.keys(body).length ? body : undefined);
}

export function finishTrip(tripId: string, at?: string): Promise<TripDto> {
  return apiPost<TripDto>(`/trips/${tripId}/finish`, at ? { at } : undefined);
}

// Cancelar NO borra: colapsa la ventana y conserva quién lo programó y por
// qué. El Operador no cancela (PRODUCT.md §4.8).
export function cancelTrip(tripId: string, reason: string): Promise<TripDto> {
  return apiPost<TripDto>(`/trips/${tripId}/cancel`, { reason });
}

// ⚠️ El backend las devuelve DESORDENADAS (issue #94): la creación aparece
// tercera. Se ordenan en cliente — ver `tripTransitionsFromDto` en mappers.ts.
export function getTripTransitions(tripId: string): Promise<{ items: TripTransitionDto[] }> {
  return apiGet<{ items: TripTransitionDto[] }>(`/trips/${tripId}/transitions`);
}

// Mismo schema `Route` que `/vehicles/{id}/route`, con los bordes del viaje
// puestos por el servidor. Si el viaje se corrige, esta respuesta cambia sola:
// NO se cachea por id de viaje.
export function getTripRoute(tripId: string, maxPoints?: number): Promise<RouteDto> {
  return apiGet<RouteDto>(`/trips/${tripId}/route${toQueryString({ maxPoints })}`);
}

export interface TripAlertsQuery {
  page?: number;
  pageSize?: number;
  severity?: Schemas['Severity'];
  category?: Schemas['AlertCategory'];
}

export function getTripAlerts(tripId: string, query: TripAlertsQuery = {}): Promise<Page<AlertDto>> {
  return apiGet<Page<AlertDto>>(`/trips/${tripId}/alerts${toQueryString(query)}`);
}

export function getAllTripAlerts(tripId: string): Promise<AlertDto[]> {
  return fetchAllPages((page, pageSize) => getTripAlerts(tripId, { page, pageSize }));
}

// ---------------------------------------------------------------------------
// FE-13 — novedades y sus cambios (BE-29, ADR-015 + ADR-024). Los nueve endpoints.
// ---------------------------------------------------------------------------

// El catálogo es POR TENANT y con semilla: producto lo ajusta sin migración
// (ADR-015 §50). `code` es el eje estable que los reportes referencian; `label`
// es lo que se muestra y sí puede cambiar.
export function getLogbookEntryTypes(active?: boolean): Promise<{ items: LogbookEntryTypeDto[] }> {
  return apiGet<{ items: LogbookEntryTypeDto[] }>(`/logbook-entry-types${toQueryString({ active })}`);
}

// Solo Administrador: la taxonomía es del tenant entero, y un Supervisor que
// pudiera inventar tipos partiría en dos los reportes de toda la empresa.
// `code` se normaliza a mayúsculas en el servidor. 409 si ya existe.
export function createLogbookEntryType(data: LogbookEntryTypeCreateDto): Promise<LogbookEntryTypeDto> {
  return apiPost<LogbookEntryTypeDto>('/logbook-entry-types', data);
}

// No hay DELETE y es a propósito: borrar un tipo reescribiría el significado
// del histórico. `active: false` lo retira del formulario y deja intactas las
// novedades que ya lo usan.
export function updateLogbookEntryType(typeId: string, data: LogbookEntryTypePatchDto): Promise<LogbookEntryTypeDto> {
  return apiPatch<LogbookEntryTypeDto>(`/logbook-entry-types/${typeId}`, data);
}

export interface LogbookQuery {
  page?: number;
  pageSize?: number;
  entryTypeId?: string;
  vehicleId?: string;
  driverId?: string;
  tripId?: string;
  subunitId?: string;
  from?: string;
  to?: string;
}

// ⚠️ `from`/`to` filtran por `occurredAt` — CUÁNDO OCURRIÓ, no cuándo se
// digitó. Una varada de anteayer relatada hoy no sale en la consulta de hoy, y
// eso es lo correcto: pertenece al día en que pasó.
//
// La visibilidad por rol la aplica el servidor (§4.8): el Operador ve solo las
// propias, el Supervisor las de su sub-unidad. Este cliente NO vuelve a
// filtrar — hacerlo sería fingir una autorización que no tenemos.
export function getLogbookEntries(query: LogbookQuery = {}): Promise<Page<LogbookEntryDto>> {
  return apiGet<Page<LogbookEntryDto>>(`/logbook-entries${toQueryString(query)}`);
}

export function getAllLogbookEntries(query: Omit<LogbookQuery, 'page' | 'pageSize'> = {}): Promise<LogbookEntryDto[]> {
  return fetchAllPages((page, pageSize) => getLogbookEntries({ ...query, page, pageSize }));
}

export function getLogbookEntry(entryId: string): Promise<LogbookEntryDto> {
  return apiGet<LogbookEntryDto>(`/logbook-entries/${entryId}`);
}

// Al menos uno de vehicleId/driverId/tripId es obligatorio: una novedad suelta
// no se puede reportar ni auditar → 400.
//
// ⚠️ Si quien registra es un OPERADOR, el servidor le estampa su propio
// `driverId` pase lo que pase en el cuerpo. No es cosmético: sin eso la novedad
// nacería fuera de su propia visibilidad —la crearía y no volvería a verla—.
export function createLogbookEntry(data: LogbookEntryCreateDto): Promise<LogbookEntryDto> {
  return apiPost<LogbookEntryDto>('/logbook-entries', data);
}

// `reason` es obligatoria: corregir el relato de un accidente sin decir por
// qué no es auditable. Para QUITAR una asociación se usa `unset: ['vehicleId']`
// y no `null` — en JSON un campo ausente y uno en `null` llegan indistinguibles
// al backend, así que el contrato lo hace explícito.
//
// Si nada cambia responde 200 con la novedad tal cual y NO anota en la
// novedad: una novedad llena de entradas vacías es una que nadie lee.
export function updateLogbookEntry(entryId: string, data: LogbookEntryPatchDto): Promise<LogbookEntryDto> {
  return apiPatch<LogbookEntryDto>(`/logbook-entries/${entryId}`, data);
}

// Quién registró y quién corrigió, con el "de qué a qué" de cada campo.
// Append-only por privilegio ausente en la base: ni el core puede reescribirla.
export function getLogbookTransitions(entryId: string): Promise<{ items: LogbookTransitionDto[] }> {
  return apiGet<{ items: LogbookTransitionDto[] }>(`/logbook-entries/${entryId}/transitions`);
}

// El expediente del viaje (ADR-024): las novedades del vehículo —o de su
// conductor— cuyo `occurredAt` cae dentro de la ventana, MÁS las que alguien
// asignó con `tripId`. No se cachea por id de viaje: corregir la ventana
// reescribe esta respuesta sin tocar una sola novedad.
export function getTripLogbookEntries(tripId: string): Promise<{ items: LogbookEntryDto[] }> {
  return apiGet<{ items: LogbookEntryDto[] }>(`/trips/${tripId}/logbook-entries`);
}

// ───────────────────────── FE-12 · gastos y recibo (BE-28, ADR-015) ─────────────────────────
//
// Once endpoints. Tres trampas del contrato valen para todos y están anotadas
// donde muerden: el importe viaja como TEXTO (`Money`), desasociar es `unset` y
// no `null`, y el recibo sube MULTIPART al core aunque baje por URL firmada.

// El catálogo es POR TENANT con semilla por defecto (ADR-015): producto lo
// ajusta sin migración. Se pide SIN filtro a propósito y se reparte en memoria
// —ver `useExpenseCategories`—: el formulario solo puede ofrecer activas, pero
// Administración necesita las inactivas para reactivarlas y el filtro del
// listado también, porque un gasto viejo conserva su categoría aunque hoy esté
// retirada.
export function getExpenseCategories(active?: boolean): Promise<{ items: ExpenseCategoryDto[] }> {
  return apiGet<{ items: ExpenseCategoryDto[] }>(`/expense-categories${toQueryString({ active })}`);
}

// Solo Administrador (`ActionManageExpenseCatalog`): la taxonomía es del tenant
// entero, y un Supervisor inventando categorías partiría en dos los reportes de
// toda la empresa. `code` se normaliza a mayúsculas sin espacios; duplicarlo
// devuelve 409.
export function createExpenseCategory(data: ExpenseCategoryCreateDto): Promise<ExpenseCategoryDto> {
  return apiPost<ExpenseCategoryDto>('/expense-categories', data);
}

// ⚠️ NO existe DELETE, y es a propósito: borrar una categoría reescribiría el
// significado del histórico de gastos. `active: false` la retira del formulario
// y deja intactos los gastos que ya la usan. `code` tampoco se corrige.
export function updateExpenseCategory(categoryId: string, data: ExpenseCategoryPatchDto): Promise<ExpenseCategoryDto> {
  return apiPatch<ExpenseCategoryDto>(`/expense-categories/${categoryId}`, data);
}

export interface ExpenseQuery {
  page?: number;
  pageSize?: number;
  categoryId?: string;
  vehicleId?: string;
  driverId?: string;
  tripId?: string;
  subunitId?: string;
  from?: string;
  to?: string;
}

// ⚠️ `from`/`to` filtran por `incurredAt` — CUÁNDO SE INCURRIÓ el gasto, no
// cuándo se digitó. Un peaje de septiembre registrado en octubre pertenece a
// septiembre y no sale en la consulta de octubre.
//
// La visibilidad por rol la aplica el servidor (§4.8): el Operador ve solo los
// propios, el Supervisor los de su sub-unidad —por la del vehículo O la del
// conductor, porque un viático no tiene vehículo—, Administrador y Ejecutivo el
// consolidado. Este cliente NO vuelve a filtrar.
export function getExpenses(query: ExpenseQuery = {}): Promise<Page<ExpenseDto>> {
  return apiGet<Page<ExpenseDto>>(`/expenses${toQueryString(query)}`);
}

export function getAllExpenses(query: Omit<ExpenseQuery, 'page' | 'pageSize'> = {}): Promise<ExpenseDto[]> {
  return fetchAllPages((page, pageSize) => getExpenses({ ...query, page, pageSize }));
}

export function getExpense(expenseId: string): Promise<ExpenseDto> {
  return apiGet<ExpenseDto>(`/expenses/${expenseId}`);
}

// Al menos uno de vehicleId/driverId/tripId es obligatorio: un gasto suelto no
// se puede reportar ni auditar → 400.
//
// ⚠️ `amount` es TEXTO con el patrón `^[0-9]{1,12}(\.[0-9]{1,2})?$`. Mandar un
// número, o el "50.000" que escribe una persona en Colombia, devuelve 400:
// normalízalo antes con `parseMontoCOP` (`utils/expenses.ts`).
//
// ⚠️ Si quien registra es un OPERADOR, el servidor le estampa su propio
// `driverId` pase lo que pase en el cuerpo. Sin eso el gasto nacería fuera de
// su propia visibilidad —lo crearía y no volvería a verlo—.
//
// `tripId` significa INTENCIÓN y es opcional: no hace falta para que el gasto
// salga en el expediente del viaje, de eso se encarga la ventana (ADR-024).
export function createExpense(data: ExpenseCreateDto): Promise<ExpenseDto> {
  return apiPost<ExpenseDto>('/expenses', data);
}

// Corrección AUDITADA: `reason` es obligatoria porque la corrección entra a la
// novedad, y corregir un registro contable sin decir por qué no es auditable.
//
// ⚠️ Para QUITAR una asociación o la nota se usa `unset: ['vehicleId']`, NUNCA
// `null`: en Go un campo ausente y uno en `null` llegan indistinguibles a un
// `*T`, así que un contrato que prometiera distinguirlos estaría mintiendo.
// Quitar la ÚLTIMA asociación devuelve 400.
//
// ⚠️ Si nada cambia responde 200 y NO ANOTA NADA. Ese 200 se leería en pantalla
// como "se guardó" cuando no se guardó nada — el formulario lo detecta antes de
// llamar.
export function updateExpense(expenseId: string, data: ExpensePatchDto): Promise<ExpenseDto> {
  return apiPatch<ExpenseDto>(`/expenses/${expenseId}`, data);
}

// Quién registró y quién corrigió, con el "de qué a qué" de cada campo.
// Append-only por privilegio en la base: ni el core puede reescribirla.
export function getExpenseTransitions(expenseId: string): Promise<{ items: ExpenseTransitionDto[] }> {
  return apiGet<{ items: ExpenseTransitionDto[] }>(`/expenses/${expenseId}/transitions`);
}

// TODAS las versiones del recibo, la vigente primero. El recibo se VERSIONA:
// cada subida es un objeto nuevo y una fila nueva, y la foto equivocada
// sobrevive como auditoría — no se puede borrar.
export function getExpenseReceipts(expenseId: string): Promise<{ items: ReceiptDto[] }> {
  return apiGet<{ items: ReceiptDto[] }>(`/expenses/${expenseId}/receipts`);
}

// ⚠️ MULTIPART al core, no URL firmada de subida. El core es el portero
// (ADR-009): calcula el SHA-256 de los bytes que recibe, escribe el objeto
// write-once y registra la fila. Con URL firmada tendría que creerse un hash
// que le manda el cliente.
//
// ⚠️ El campo se llama `file` y su `Content-Type` sale del `type` del File: el
// backend lee la cabecera de la PARTE (`leerRecibo`), no la de la petición. Un
// blob sin tipo llega vacío y responde 400.
//
// Formatos: image/jpeg, image/png, image/heic, image/webp, application/pdf.
// Tope 10 MiB → 400 (el contrato declara 413, el core responde 400 por
// `ErrInvalidInput`; `mensajeErrorGasto` traduce los dos).
//
// Subir de nuevo NO reemplaza: crea una versión que pasa a ser la vigente.
export function uploadExpenseReceipt(expenseId: string, file: File): Promise<ReceiptDto> {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiPostForm<ReceiptDto>(`/expenses/${expenseId}/receipts`, form);
}

// ⚠️ Es un POST, no un GET: emite una capacidad temporal sobre un objeto
// concreto tras comprobar RLS y alcance (ADR-009 §1). Se pide AL ABRIR el
// recibo, nunca al listar los gastos: una URL firmada por fila es N+1 y además
// caduca mientras el usuario mira la lista.
//
// Sirve igual para la versión vigente y para las anteriores — auditar el recibo
// original es justamente para lo que se conservan.
export function getReceiptDownloadUrl(expenseId: string, receiptId: string): Promise<{ url: string; expiresAt: string }> {
  return apiPost<{ url: string; expiresAt: string }>(`/expenses/${expenseId}/receipts/${receiptId}/download-url`);
}

// ───────── adjuntos de una novedad (BE-70 · ADR-009) ─────────
//
// ⚠️ MULTIPART al core, no URL firmada de subida — igual que el recibo del
// gasto y por el mismo motivo: el core calcula el SHA-256 de los bytes que
// recibe. Con URL firmada tendría que creerse un hash que le manda el cliente.
//
// ⚠️ El campo se llama `file` y su `Content-Type` sale del `type` del File: el
// backend lee la cabecera de la PARTE, no la de la petición.
//
// Formatos: image/jpeg, image/png, image/heic, image/webp, application/pdf.
// Tope 10 MiB → **400** (y aquí el contrato SÍ lo declara así: BE-70 retiró el
// 413 fantasma que el molde de recibos arrastraba). Pasarse de 10 adjuntos por
// novedad → **409**: ahí el archivo está bien y lo que sobra es el sitio.
//
// Subir otra foto NO reemplaza ninguna: añade. Y no hay forma de borrarla.
export function uploadLogbookAttachment(
  entryId: string,
  file: File,
  onProgress?: (porcentaje: number) => void,
): Promise<LogbookAttachmentDto> {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiPostFormWithProgress<LogbookAttachmentDto>(
    `/logbook-entries/${entryId}/attachments`, form, onProgress);
}

// La lista sale cronológica —la más antigua primero—, al revés que las
// versiones de un recibo: aquí no hay "vigente", hay una galería.
export function getLogbookAttachments(entryId: string): Promise<{ items: LogbookAttachmentDto[] }> {
  return apiGet<{ items: LogbookAttachmentDto[] }>(`/logbook-entries/${entryId}/attachments`);
}

// POST, no GET: emite una capacidad temporal sobre un objeto concreto (ADR-009
// §1). Se pide AL ABRIR la foto, nunca al listar: una URL firmada por miniatura
// es N+1 y además caduca mientras el usuario mira.
//
// Ver no lleva permiso propio: el alcance lo pone la consulta. Por eso el
// Ejecutivo, que no puede adjuntar, sí descarga.
export function getLogbookAttachmentDownloadUrl(
  entryId: string,
  attachmentId: string,
): Promise<{ url: string; expiresAt: string }> {
  return apiPost<{ url: string; expiresAt: string }>(
    `/logbook-entries/${entryId}/attachments/${attachmentId}/download-url`);
}

// El expediente del viaje (ADR-024): los gastos del vehículo —o de su
// conductor— cuyo `incurredAt` cae dentro de la ventana, MÁS los que alguien
// asignó con `tripId`. Un gasto con `tripId` de OTRO viaje nunca aparece aquí
// aunque su fecha caiga dentro: la intención declarada manda sobre la ventana.
// No se cachea por id de viaje: corregir la ventana reescribe esta respuesta
// sin tocar un solo gasto.
export function getTripExpenses(tripId: string): Promise<{ items: ExpenseDto[] }> {
  return apiGet<{ items: ExpenseDto[] }>(`/trips/${tripId}/expenses`);
}

// Los ingresos del viaje, DERIVADOS de su ventana igual que los gastos
// (ADR-024). Se piden siempre, incluso sin ventana.
export function getTripRevenues(tripId: string): Promise<{ items: RevenueDto[] }> {
  return apiGet<{ items: RevenueDto[] }>(`/trips/${tripId}/revenues`);
}

// ---------------------------------------------------------------------------
// FE-32 — finanzas: clientes, tarifas, ingresos y resultado (ADR-030, BE-73)
// ---------------------------------------------------------------------------

// Lo ven todos los roles que registran ingresos: el Operador lo necesita para el
// formulario del móvil.
export function getCustomers(active?: boolean): Promise<{ items: CustomerDto[] }> {
  return apiGet<{ items: CustomerDto[] }>(`/customers${toQueryString({ active })}`);
}

// Administrador y Ejecutivo (`ActionManageTariffs`). El nombre es único por
// tenant → 409.
export function createCustomer(data: CustomerCreateDto): Promise<CustomerDto> {
  return apiPost<CustomerDto>('/customers', data);
}

// Sin DELETE: un cliente con ingresos es historia contable. Se desactiva.
export function updateCustomer(customerId: string, data: CustomerPatchDto): Promise<CustomerDto> {
  return apiPatch<CustomerDto>(`/customers/${customerId}`, data);
}

export interface TariffsQuery {
  customerId?: string;
  /** `YYYY-MM-DD`: solo las vigentes ese día (`validTo` inclusivo). Es la consulta del formulario. */
  validAt?: string;
  /** Las de esa clase Y las que no distinguen clase. */
  vehicleClass?: string;
  active?: boolean;
}

export function getTariffs(query: TariffsQuery = {}): Promise<{ items: TariffDto[] }> {
  return apiGet<{ items: TariffDto[] }>(`/tariffs${toQueryString(query)}`);
}

// Cada forma exige sus parámetros (FLAT: fixedAmount; PER_UNIT: unit +
// unitPrice; FIXED_PLUS_PER_UNIT: los tres). `discountPct` es porcentaje (8 =
// 8 %), `fixedAmount`/`minimumAmount` son `Money` texto y `unitPrice` texto con
// hasta cuatro decimales.
export function createTariff(data: TariffCreateDto): Promise<TariffDto> {
  return apiPost<TariffDto>('/tariffs', data);
}

// Solo nombre, cierre de vigencia (`validTo`) e interruptor. Los precios NO se
// corrigen: cada ingreso guarda la foto de su tarifa, y reescribir los números
// dejaría la lista diciendo algo distinto de lo que se cobró. Para cambiar
// precios: crear otra tarifa y cerrar esta.
export function updateTariff(tariffId: string, data: TariffPatchDto): Promise<TariffDto> {
  return apiPatch<TariffDto>(`/tariffs/${tariffId}`, data);
}

// La cuenta del cuaderno, en el servidor y sin efectos. El formulario la llama
// mientras el usuario teclea la cantidad; `POST /revenues` la repite y guarda el
// resultado. `quantity` es obligatoria salvo en FLAT.
export function quoteTariff(tariffId: string, quantity?: string): Promise<TariffQuoteDto> {
  return apiPost<TariffQuoteDto>(`/tariffs/${tariffId}/quote`, quantity ? { quantity } : {});
}

export interface RevenueQuery {
  customerId?: string;
  tariffId?: string;
  vehicleId?: string;
  driverId?: string;
  tripId?: string;
  subunitId?: string;
  /** Por `occurredAt`: cuándo se PRESTÓ el servicio, no cuándo se digitó. */
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export function getRevenues(query: RevenueQuery = {}): Promise<Page<RevenueDto>> {
  return apiGet<Page<RevenueDto>>(`/revenues${toQueryString(query)}`);
}

export function getAllRevenues(query: Omit<RevenueQuery, 'page' | 'pageSize'> = {}): Promise<RevenueDto[]> {
  return fetchAllPages((page, pageSize) => getRevenues({ ...query, page, pageSize }));
}

export function getRevenue(revenueId: string): Promise<RevenueDto> {
  return apiGet<RevenueDto>(`/revenues/${revenueId}`);
}

// Al menos uno de vehicleId/driverId/tripId. Con `tariffId` el core cotiza en
// el servidor: si `amount` difiere de lo propuesto, `overrideReason` es
// obligatoria (400 sin ella). El Operador queda con su propio conductor pase lo
// que pase en el cuerpo. `amount` normalizado antes con `parseMontoCOP`,
// `quantity` con `parseCantidad`.
export function createRevenue(data: RevenueCreateDto): Promise<RevenueDto> {
  return apiPost<RevenueDto>('/revenues', data);
}

// Solo lo que cambia + `reason`. Quitar algo es `unset: [...]`, nunca `null`.
// Cambiar tarifa o cantidad vuelve a cotizar en el servidor.
export function updateRevenue(revenueId: string, data: RevenuePatchDto): Promise<RevenueDto> {
  return apiPatch<RevenueDto>(`/revenues/${revenueId}`, data);
}

export function getRevenueTransitions(revenueId: string): Promise<{ items: RevenueTransitionDto[] }> {
  return apiGet<{ items: RevenueTransitionDto[] }>(`/revenues/${revenueId}/transitions`);
}

export function getRevenueAttachments(revenueId: string): Promise<{ items: RevenueAttachmentDto[] }> {
  return apiGet<{ items: RevenueAttachmentDto[] }>(`/revenues/${revenueId}/attachments`);
}

// Mismo molde multipart que el recibo del gasto: campo `file`, el core calcula
// el hash y escribe write-once bajo `revenues/`. Mismos formatos y tope.
export function uploadRevenueAttachment(revenueId: string, file: File): Promise<RevenueAttachmentDto> {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiPostForm<RevenueAttachmentDto>(`/revenues/${revenueId}/attachments`, form);
}

export function getRevenueAttachmentDownloadUrl(revenueId: string, attachmentId: string): Promise<{ url: string; expiresAt: string }> {
  return apiPost<{ url: string; expiresAt: string }>(`/revenues/${revenueId}/attachments/${attachmentId}/download-url`);
}

export interface FinanceSummaryQuery {
  /** `YYYY-MM-DD`, día local del tenant. `to` inclusivo. */
  from: string;
  to: string;
  groupBy: FinanceGroupByDto;
  subunitId?: string;
  vehicleId?: string;
  customerId?: string;
}

// Lee los AGREGADOS del servidor (ADR-016), nunca suma en el cliente. Es lo que
// reemplaza la suma de `ExpensesStatsPanel` (FE-29). El Operador recibe 403.
export function getFinanceSummary(query: FinanceSummaryQuery): Promise<FinanceSummaryDto> {
  return apiGet<FinanceSummaryDto>(`/finance/summary${toQueryString(query)}`);
}

// --- Chat (FE-21, ADR-026) ---
//
// El front NUNCA escribe a Firestore: las reglas lo niegan (`allow write: if
// false`). Se envía por REST, `chatLive.ts` solo avisa que algo cambió.

// La matriz de contactos (quién puede hablar con quién) ya la resuelve el
// backend — el front no la reconstruye con `useDrivers()`/`useUsers()`.
export function getChatContacts(): Promise<{ items: ChatContactDto[] }> {
  return apiGet<{ items: ChatContactDto[] }>('/chat/contacts');
}

// El contrato de chat no trae `Page`/`totalItems`: se recorre mientras una
// página vuelva llena, no contra un total declarado.
async function fetchWhileFull<T>(fetchPage: (page: number, pageSize: number) => Promise<{ items: T[] }>): Promise<T[]> {
  const pageSize = 200;
  const items: T[] = [];
  let page = 1;
  for (;;) {
    const res = await fetchPage(page, pageSize);
    items.push(...res.items);
    if (res.items.length < pageSize) break;
    page += 1;
  }
  return items;
}

export function getConversations(page = 1, pageSize = 200): Promise<{ items: ConversationDto[] }> {
  return apiGet<{ items: ConversationDto[] }>(`/conversations${toQueryString({ page, pageSize })}`);
}

// La lista de conversaciones pinta la barra lateral entera: a diferencia de
// los mensajes, aquí sí interesa tenerla completa de una vez.
export function getAllConversations(): Promise<ConversationDto[]> {
  return fetchWhileFull((page, pageSize) => getConversations(page, pageSize));
}

// Idempotente: si ya existía la conversación con esa persona, el backend
// devuelve la que hay (200) en vez de crear una segunda.
export function openConversation(withUserId: string): Promise<ConversationDto> {
  return apiPost<ConversationDto>('/conversations', { withUserId });
}

// Paginado a propósito — es el historial anterior a la ventana viva, no algo
// para recorrer entero de una vez (a diferencia de `getAllConversations`).
export function getMessages(conversationId: string, page = 1, pageSize = 50): Promise<{ items: MessageDto[] }> {
  return apiGet<{ items: MessageDto[] }>(`/conversations/${conversationId}/messages${toQueryString({ page, pageSize })}`);
}

// Mensaje de solo texto. Para adjunto, usar sendChatAttachment (multipart).
export function sendChatMessage(conversationId: string, body: string): Promise<MessageDto> {
  return apiPost<MessageDto>(`/conversations/${conversationId}/messages`, { body });
}

// ⚠️ Multipart al core, igual que el recibo de un gasto (ADR-009): el
// SHA-256 lo calcula quien guarda el archivo, nunca el cliente. `onProgress`
// existe porque un video de hasta 50 MiB sin barra de progreso es
// indistinguible de una subida colgada.
export function sendChatAttachment(
  conversationId: string,
  file: File,
  body: string | undefined,
  onProgress?: (fraction: number) => void,
): Promise<MessageDto> {
  const form = new FormData();
  if (body) form.append('body', body);
  form.append('file', file, file.name);
  return apiPostFormWithProgress<MessageDto>(`/conversations/${conversationId}/messages`, form, onProgress);
}

// Idempotente — llamarlo dos veces dado el mismo estado deja lo mismo.
export function markConversationRead(conversationId: string): Promise<void> {
  return apiPost<void>(`/conversations/${conversationId}/read`);
}

// La URL es una capacidad temporal que NO se persiste (ADR-014): pídela al
// abrir el adjunto, nunca al listar la conversación.
export function getMessageAttachmentDownloadUrl(messageId: string, attachmentId: string): Promise<{ url: string; expiresAt: string }> {
  return apiPost<{ url: string; expiresAt: string }>(`/messages/${messageId}/attachments/${attachmentId}/download-url`);
}

function toQueryString<T extends object>(params: T): string {
  const entries = Object.entries(params as Record<string, unknown>).filter(
    (entry): entry is [string, string | number] => entry[1] !== undefined,
  );
  if (entries.length === 0) return '';
  return `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`;
}

// ---------------------------------------------------------------------------
// FE-32 — reportes descargables (BE-22, PRODUCT.md §4.6). Primera conexión del
// frontend a `/reports`: hasta aquí la exportación era un CSV armado en el
// navegador (`utils/csv.ts`, retirado).
// ---------------------------------------------------------------------------

// Responde 202 con el reporte en GENERATING; el archivo se consulta por su id.
// En la práctica el core lo genera en la misma petición y el estado ya vuelve
// READY, pero el contrato no lo promete: se hace polling igual.
export function createReport(data: ReportRequestDto): Promise<ReportDto> {
  return apiPost<ReportDto>('/reports', data);
}

export function getReport(reportId: string): Promise<ReportDto> {
  return apiGet<ReportDto>(`/reports/${reportId}`);
}

// 201 con la URL firmada; 409 si todavía no está listo.
export function getReportDownloadUrl(reportId: string): Promise<{ url: string; expiresAt: string }> {
  return apiPost<{ url: string; expiresAt: string }>(`/reports/${reportId}/download-url`);
}
